import { useEffect } from 'react'

import { fetchPropertyCalendarPrices } from 'actions/PropertyActions'
import { EmptyObject } from 'lib/object/objectUtils'
import buildPropertyCalendarPricesKey from 'lib/offer/buildPropertyCalendarPricesKey'

import { useAppDispatch, useAppSelector } from './reduxHooks'

interface PropertyCalendarPricesHookParams {
  propertyId: string
  duration?: number
  occupancies?: Array<App.Occupants>
}

function usePropertyCalendarPrices({
  propertyId,
  duration,
  occupancies,
}: PropertyCalendarPricesHookParams): [{ [isoDate: string]: App.Property.CalendarDay }, boolean, string | undefined] {
  const dispatch = useAppDispatch()
  const regionCode = useAppSelector(state => state.geo.currentRegionCode)
  const currency = useAppSelector(state => state.geo.currentCurrency)

  const key = buildPropertyCalendarPricesKey({
    propertyId,
    duration,
    occupancies,
    regionCode,
    currency,
  })
  const record = useAppSelector(state => state.offer.propertyCalendarPrices[key])

  const isFetchRequired = !!key && !record
  useEffect(() => {
    if (isFetchRequired) {
      dispatch(fetchPropertyCalendarPrices(propertyId, {
        duration: duration!,
        occupancies: occupancies!,
      }))
    }
    // NB: `key` effectively (validates and) incorporates `propertyId`, `duration`,
    // `occupancies`, `regionCode`, and `currency`.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isFetchRequired, key])

  return [
    record?.days ?? EmptyObject,
    record?.fetching ?? isFetchRequired,
    record?.error,
  ]
}

export default usePropertyCalendarPrices
